import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@utilities-one/gpm-ui';
import { toast } from 'sonner';

import { type MarketOption, MarketSelect } from '@/entities/user/components/MarketSelect';
import { useSwitchMarket } from '@/entities/user/query/user.mutations';
import { enterApp } from '@/entities/user/utils/enterApp';
import { extractApiError } from '@/library/api/extractApiError';
import { DialogPortalLayer } from '@/library/portal/DialogPortalLayer';

interface Props {
  markets: MarketOption[];
}

/**
 * UG-4847 — the mandatory post-login market picker for multi-market users
 * (the caller guarantees at least two markets; the first is preselected):
 * always open while mounted, dismissal ignored and the X suppressed — the
 * only way forward is Confirm. Confirm switches the market on the API and
 * boots the app with a full reload — hence `invalidate: false`, every boot
 * query runs fresh under the picked market anyway. On error the dialog stays
 * open for another pick.
 */
export const MarketSelectDialog = ({ markets }: Props) => {
  const switchMarket = useSwitchMarket({ invalidate: false });
  const [marketId, setMarketId] = useState(markets[0].id);

  const pending = switchMarket.isPending || switchMarket.isSuccess;

  const onConfirm = () => {
    switchMarket.mutate(
      { marketId },
      {
        onSuccess: enterApp,
        onError: (error) => toast.error(extractApiError(error, 'Failed to switch market')),
      }
    );
  };

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Select Market</DialogTitle>
          <DialogDescription>
            Choose the market to enter. You can switch markets later from the top bar.
          </DialogDescription>
        </DialogHeader>
        <DialogPortalLayer>
          <MarketSelect
            markets={markets}
            value={marketId}
            onValueChange={setMarketId}
            disabled={pending}
            className="w-full"
            aria-label="Market"
          />
          <DialogFooter>
            <Button type="button" disabled={pending} onClick={onConfirm}>
              {pending && <Spinner data-icon="inline-start" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogPortalLayer>
      </DialogContent>
    </Dialog>
  );
};
